import * as React from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  classifyLoadFailure,
  loadFailureMessage,
  type LoadFailureKind,
} from '../../lib/load-failure';

/**
 * THE THIRD BRANCH, rendered exactly where the empty state would have been.
 *
 * The reasoning, the three states and the refusal/unreachable split all live
 * in `lib/load-failure.ts`. This is only the rendering, and it is deliberately
 * the plainest thing that could work:
 *
 *   - NO RETRY BUTTON. React Query has already retried whatever is worth
 *     retrying, and a refusal is not. A button that re-runs a query the server
 *     will refuse again is a second way of saying nothing, and it invites the
 *     reader to keep pressing it instead of reading the sentence.
 *   - NO ILLUSTRATION. One glyph, sized to the empty states it replaces, so
 *     the panel does not change shape between states and the difference the
 *     reader has to notice is the words.
 *   - It goes INSIDE the register panel, not in a banner at the top. A banner
 *     leaves the empty state visible underneath it, which is the bug with an
 *     apology stapled on -- the same mistake `RowError` exists to avoid one
 *     row at a time.
 *
 * WHY THE GLYPH DIFFERS BY KIND and the colour does not. A refusal is not a
 * fault and should not be red: nothing is broken, the reader simply is not
 * allowed, and a red alarm on a working system trains people to ignore red.
 * Both sit on the muted ink the empty states use, and the glyph is the signal.
 */
export interface LoadFailureNoticeProps {
  /**
   * The query's error. Passed raw so a call site can hand over `error`
   * directly; a falsy value renders nothing, so this is safe to mount
   * unconditionally beside a `data.length === 0` branch.
   */
  error: unknown;
  /**
   * Lowercase plural noun phrase naming what failed to load, as it reads
   * mid-sentence: 'invoices', 'time-off requests', 'equipment alerts'.
   */
  subject: string;
  /** The reader's role label, when the screen knows it. Refusals only. */
  roleLabel?: string;
  /** Who IS allowed, e.g. 'admins and office staff'. Refusals only. */
  allowed?: string;
  className?: string;
  /**
   * Override the classification.
   *
   * For the one case the error object cannot answer: a screen that fetches
   * several lists and has to say which of them failed renders one notice per
   * failed source, and a source can be known-refused without an error in hand.
   */
  kind?: LoadFailureKind;
}

export function LoadFailureNotice({
  error,
  subject,
  roleLabel,
  allowed,
  className,
  kind: kindOverride,
}: LoadFailureNoticeProps) {
  const kind = kindOverride ?? classifyLoadFailure(error);
  if (!kind) return null;

  const { heading, detail } = loadFailureMessage(kind, subject, { roleLabel, allowed });
  const Glyph = kind === 'refused' ? Lock : AlertTriangle;

  return (
    <div
      // A live region rather than a plain div: on every call site this
      // replaces content that was already on screen, so a screen reader that
      // has finished reading the panel gets no second announcement otherwise.
      role="status"
      data-testid="load-failure-notice"
      data-load-failure-kind={kind}
      className={cn('px-4 py-12 text-center', className)}
    >
      <Glyph aria-hidden="true" className="mx-auto mb-3 size-10 text-fieldiq-muted" />
      <p className="text-sm text-fieldiq-text">{heading}</p>
      <p className="mx-auto mt-1 max-w-[46ch] text-xs text-fieldiq-muted">{detail}</p>
    </div>
  );
}

export default LoadFailureNotice;
